import { UsersShowResource } from "../http/apiTypes";
import { ProvisionedUser } from "./types";

type ShouldProvisionAuth0UserParams = {
  isLoading: boolean;
  isAuth0Authenticated: boolean;
  hasAttemptedProvision: boolean;
};

/**
 * Provisioning gate that decides whether an Auth0-authenticated user still
 * needs a Rails User record.
 */
export function shouldProvisionAuth0User({
  isLoading,
  isAuth0Authenticated,
  hasAttemptedProvision,
}: ShouldProvisionAuth0UserParams): boolean {
  if (isLoading) return false;
  if (!isAuth0Authenticated) return false;
  return !hasAttemptedProvision;
}

/**
 * Builds minimal app user model from provision response and Auth0 claims.
 */
type BuildProvisionedUserParams = {
  id: string | number;
  auth0User?: {
    email?: string;
    name?: string;
  };
  userResource?: UsersShowResource;
};

export function buildProvisionedUser({ id, auth0User, userResource }: BuildProvisionedUserParams): ProvisionedUser {
  const organizations =
    userResource?.relationships?.organizations?.data
      .map(organization => Number(organization.id))
      .filter(organizationId => !Number.isNaN(organizationId)) || [];

  const resolvedId = userResource?.id || String(id);
  const resolvedEmail = userResource?.attributes.email || auth0User?.email || "";
  const resolvedName = userResource?.attributes.name || auth0User?.name || auth0User?.email || "Auth0 User";

  const employeeRegion = userResource?.attributes.employee_region;
  const employeeType = userResource?.attributes.employee_type;

  if (userResource?.attributes.admin) {
    return {
      id: String(resolvedId),
      email: resolvedEmail,
      name: resolvedName,
      organizations,
      detailed_report_organizations: organizations,
      admin: true,
      employee_region: employeeRegion,
      employee_type: employeeType,
    };
  }

  return {
    id: String(resolvedId),
    email: resolvedEmail,
    name: resolvedName,
    organizations,
    detailed_report_organizations: organizations,
    admin: false,
  };
}
